import { Badge, Box, Card, Flex, HStack, Spinner, Stack, Text } from '@chakra-ui/react'
import { useQuery } from '@tanstack/react-query'
import { useParams } from 'react-router'
import { getAgendaMembersService, type AgendaDetails, type AgendaMember } from '@/services/agendas'
import { ROLE_CONFIG } from '@/utils/agendaRoles'
import { formatDate } from '@/utils/date'
import { LuUsers } from 'react-icons/lu'
import { AGENDA_PERMISSIONS, hasPermission } from '@/utils/agendaPermissions'
import RemoveMemberButton from './RemoveMemberButton'
import PromoteMemberButton from './PromoteMemberButton'
import DemoteMemberButton from './DemoteMemberButton'
import TransferOwnershipButton from './TransferOwnershipButton'
import EditMemberPermissionsButton from './EditMemberPermissionsButton'
import { useMe } from '@/hooks/auth/useMe'

type Props = {
  agenda: AgendaDetails,
}

type MemberActionsProps = {
  agendaId: string
  member: AgendaMember
  agendaRole: AgendaDetails['role']
  canRemoveMembers: boolean
  canManagePermissions: boolean
  isMe: boolean
}

const MemberActions = ({ agendaId, member, agendaRole, canRemoveMembers, canManagePermissions, isMe }: MemberActionsProps) => {
  const isNotMe = !isMe;
  const isNotOwner = member.role !== 'owner';
  const isAdmin = agendaRole === 'owner' || agendaRole === 'admin';

  const canActOnMember = isNotMe && isNotOwner;

  const canPromote = isAdmin && canActOnMember && member.role !== 'admin';
  const canDemote = isAdmin && canActOnMember && member.role !== 'member';
  const canTransferOwnership = agendaRole === 'owner' && isNotOwner;
  const canRemove = canRemoveMembers && canActOnMember && (agendaRole === 'owner' || member.role !== 'admin');
  const canEditPermissions = canManagePermissions && canActOnMember && member.role === 'member';

  return (
    <Flex gap={2} align="center" flexWrap="wrap">
      {canPromote && <PromoteMemberButton agendaId={agendaId} member={member} />}
      {canDemote && <DemoteMemberButton agendaId={agendaId} member={member} />}
      {canEditPermissions && <EditMemberPermissionsButton agendaId={agendaId} member={member} />}
      {canTransferOwnership && <TransferOwnershipButton agendaId={agendaId} member={member} />}
      {canRemove && <RemoveMemberButton agendaId={agendaId} member={member} />}
    </Flex>
  )
}

const MembersTab = ({ agenda }: Props) => {
  const { agendaId } = useParams<{ agendaId: string }>()
  const { data: me } = useMe()

  const { data: members, isLoading } = useQuery({
    queryKey: ['agendas', agendaId, 'members'],
    queryFn: () => getAgendaMembersService(agendaId!),
  })

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <Spinner size="lg" color="primary.500" />
      </Box>
    )
  }

  if (!members || members.length === 0) {
    return (
      <Box display="flex" flexDir="column" alignItems="center" gap={2} py={10} color="fg.muted">
        <LuUsers size={32} />
        <Text fontSize="sm">Nenhum membro encontrado.</Text>
      </Box>
    )
  }

  const canRemoveMembers =
    agenda.role !== 'member' || hasPermission(agenda.permissions, AGENDA_PERMISSIONS.REMOVE_MEMBERS)

  const canManagePermissions =
    agenda.role !== 'member' || hasPermission(agenda.permissions, AGENDA_PERMISSIONS.MANAGE_PERMISSIONS)

  return (
    <Stack gap={3} px={4} py={4}>
      {members.map((member: AgendaMember) => (
        <Card.Root key={member.id} variant="outline" w="full">
          <Card.Body>
            <Flex
              justify="space-between"
              align={{ base: 'flex-start', sm: 'center' }}
              flexDir={{ base: 'column', sm: 'row' }}
              gap={2}
            >
              <Box>
                <HStack gap={2} align="center">
                  <Card.Title fontSize={{ base: 'md', md: 'lg' }}>
                    {member.user.name}
                  </Card.Title>
                  {me?.id === member.user.id && (
                    <Badge colorPalette="blue" size="sm">Você</Badge>
                  )}
                </HStack>
                <Text fontSize={{ base: 'xs', md: 'sm' }} color="fg.muted">
                  Entrou em: {formatDate(member.joined_at)}
                </Text>
              </Box>
              <HStack
                gap={3}
                align={{ base: 'flex-start', sm: 'flex-end', md: 'center' }}
                flexShrink={0}
                flexDir={{ base: 'column', md: 'row' }}
              >
                <Text
                  fontSize="xs"
                  fontWeight="semibold"
                  textTransform="uppercase"
                  color={ROLE_CONFIG[member.role]?.color || 'fg.muted'}
                  letterSpacing="wide"
                >
                  {ROLE_CONFIG[member.role]?.label || member.role}
                </Text>
                <MemberActions
                  agendaId={agendaId!}
                  member={member}
                  agendaRole={agenda.role}
                  canRemoveMembers={canRemoveMembers}
                  canManagePermissions={canManagePermissions}
                  isMe={me?.id === member.user.id}
                />
              </HStack>
            </Flex>
          </Card.Body>
        </Card.Root>
      ))}
    </Stack>
  )
}

export default MembersTab
